import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LockKey, Plus, LinkSimple, Trash, PencilSimple, Check, X, PaperPlaneTilt, FilePdf, UsersThree } from "@phosphor-icons/react";

const fmtBytes = (n) => n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  denied: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

function RequestRow({ r, onDecide, busy }) {
  return (
    <tr className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`request-row-${r.id}`}>
      <td className="px-3 py-2.5">
        <div className="font-medium text-zinc-900">{r.name}</div>
        <div className="text-xs text-zinc-500">{r.email}</div>
      </td>
      <td className="px-3 py-2.5 text-sm text-zinc-600">{r.company || "—"}</td>
      <td className="px-3 py-2.5 text-sm text-zinc-600">{r.publication_title}</td>
      <td className="px-3 py-2.5 text-xs text-zinc-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
      <td className="px-3 py-2.5">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${STATUS_STYLE[r.status] || ""}`} data-testid={`request-status-${r.id}`}>{r.status}</span>
      </td>
      <td className="px-3 py-2.5 text-right whitespace-nowrap">
        {r.status === "pending" ? (
          <div className="inline-flex gap-1.5">
            <button data-testid={`approve-request-${r.id}`} disabled={busy} onClick={() => onDecide(r, "approve")}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
              <Check size={12}/> Approve &amp; send
            </button>
            <button data-testid={`deny-request-${r.id}`} disabled={busy} onClick={() => onDecide(r, "deny")}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">
              <X size={12}/> Deny
            </button>
          </div>
        ) : (
          <span className="text-[11px] text-zinc-400">{r.decided_at ? fmtDate(r.decided_at) : ""}{r.decided_via ? ` · via ${r.decided_via}` : ""}</span>
        )}
      </td>
    </tr>
  );
}

function PublicationCard({ p, onCopyLink, onDelete, onSave }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const startEdit = () => { setForm({ title: p.title, description: p.description, fulfillment_subject: p.fulfillment_subject, fulfillment_message: p.fulfillment_message }); setEditing(true); };
  const save = async () => { await onSave(p.id, form); setEditing(false); };
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={`publication-card-${p.id}`}>
      {!editing ? (
        <>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-heading font-medium text-zinc-900 flex items-center gap-2"><LockKey size={15} className="text-zinc-400 shrink-0"/>{p.title}</div>
              {p.description && <p className="text-sm text-zinc-500 mt-1 whitespace-pre-line">{p.description}</p>}
              <div className="flex items-center gap-2 mt-2 text-[11px] text-zinc-500">
                <FilePdf size={13} className="text-zinc-400"/>{p.filename} · {fmtBytes(p.size)}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button data-testid={`edit-publication-${p.id}`} onClick={startEdit} className="p-1.5 text-zinc-400 hover:text-zinc-900 rounded-md hover:bg-zinc-100"><PencilSimple size={14}/></button>
              <button data-testid={`delete-publication-${p.id}`} onClick={() => onDelete(p)} className="p-1.5 text-zinc-400 hover:text-rose-600 rounded-md hover:bg-zinc-100"><Trash size={14}/></button>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-100">
            <div className="flex gap-4 text-xs text-zinc-500">
              <span><strong className="text-zinc-900">{p.stats?.requests || 0}</strong> requests</span>
              <span><strong className="text-emerald-700">{p.stats?.approved || 0}</strong> approved</span>
              <span><strong className="text-zinc-500">{p.stats?.denied || 0}</strong> denied</span>
            </div>
            <button data-testid={`copy-link-${p.id}`} onClick={() => onCopyLink(p)}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50 font-medium">
              <LinkSimple size={13}/> Copy request link
            </button>
          </div>
          {p.list_id && (
            <Link data-testid={`view-list-${p.id}`} to={`/lists/${p.list_id}`}
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900">
              <UsersThree size={13}/> View requesters list — everyone who asks is added automatically
            </Link>
          )}
        </>
      ) : (
        <div className="space-y-2">
          <div><span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Title</span>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm"/></div>
          <div><span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Description (shown on the request page)</span>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm"/></div>
          <div><span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Fulfilment email subject</span>
            <input value={form.fulfillment_subject} onChange={e => setForm({ ...form, fulfillment_subject: e.target.value })} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm"/></div>
          <div><span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Fulfilment email message</span>
            <textarea value={form.fulfillment_message} onChange={e => setForm({ ...form, fulfillment_message: e.target.value })} rows={2} placeholder="Thank you for your interest — your request has been approved." className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm"/></div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(false)} className="text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">Cancel</button>
            <button data-testid={`save-publication-${p.id}`} onClick={save} className="text-sm px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadDialog({ onClose, onCreated }) {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", fulfillment_subject: "", fulfillment_message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) { toast.error("Choose a file (e.g. the PDF of your publication)"); return; }
    if (!form.title.trim()) { toast.error("Give the publication a title"); return; }
    setBusy(true);
    try {
      await api.createPublication(file, form);
      toast.success("Publication added — copy its request link into your campaign CTA");
      onCreated();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-zinc-200 w-full max-w-lg p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="upload-publication-dialog">
        <h3 className="font-heading text-base font-medium">Add a gated publication</h3>
        <div>
          <span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Document (PDF recommended, max 10 MB)</span>
          <input data-testid="publication-file-input" type="file" onChange={e => setFile(e.target.files?.[0] || null)} className="text-sm"/>
        </div>
        <div>
          <span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Title</span>
          <input data-testid="publication-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Q2 Market Access Whitepaper" className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm"/>
        </div>
        <div>
          <span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Description <span className="normal-case font-normal">(shown on the public request page)</span></span>
          <textarea data-testid="publication-desc-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="A short teaser of what's inside…" className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm"/>
        </div>
        <div>
          <span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Fulfilment email message <span className="normal-case font-normal">(sent with the attachment when you approve)</span></span>
          <textarea value={form.fulfillment_message} onChange={e => setForm({ ...form, fulfillment_message: e.target.value })} rows={2} placeholder="Thank you for your interest — please find the publication attached." className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm"/>
        </div>
        <div className="flex gap-2 justify-end pt-1">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">Cancel</button>
          <button data-testid="publication-upload-submit" disabled={busy} onClick={submit} className="text-sm px-4 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50">{busy ? "Uploading…" : "Add publication"}</button>
        </div>
      </div>
    </div>
  );
}

export default function PublicationsPage() {
  const [tab, setTab] = useState("requests");
  const [pubs, setPubs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pending, setPending] = useState(0);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [showUpload, setShowUpload] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.listPublications().then(d => { setPubs(d.items || []); setPending(d.pending_requests || 0); });
    api.listPubRequests(statusFilter === "all" ? null : statusFilter).then(d => { setRequests(d.items || []); setPending(d.pending || 0); });
  };
  useEffect(load, [statusFilter]);

  const decide = async (r, action) => {
    setBusy(true);
    try {
      const res = action === "approve" ? await api.approvePubRequest(r.id) : await api.denyPubRequest(r.id);
      if (res.status === "approved") toast.success(`Approved — “${r.publication_title}” has been emailed to ${r.email}`);
      else toast.success(`Denied — ${r.name} will not be notified`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const copyLink = (p) => {
    const url = `${window.location.origin}/p/${p.id}`;
    navigator.clipboard.writeText(url);
    toast.success("Request link copied — paste it into a CTA button in your campaign");
  };

  const savePub = async (id, data) => {
    try { await api.updatePublication(id, data); toast.success("Saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const deletePub = async (p) => {
    if (!window.confirm(`Delete “${p.title}” and its ${p.stats?.requests || 0} request(s)? The request link in any sent email will stop working.`)) return;
    await api.deletePublication(p.id);
    toast.success("Publication deleted");
    load();
  };

  return (
    <div className="space-y-5" data-testid="publications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-medium">Gated publications</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Readers request a copy via a form; you approve or deny; approved requests are emailed the document automatically.</p>
        </div>
        <button data-testid="add-publication-btn" onClick={() => setShowUpload(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-zinc-950 text-white hover:bg-zinc-800">
          <Plus size={14}/> Add publication
        </button>
      </div>

      <div className="flex gap-1 border-b border-zinc-200">
        <button data-testid="tab-requests" onClick={() => setTab("requests")} className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "requests" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
          Requests {pending > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">{pending}</span>}
        </button>
        <button data-testid="tab-library" onClick={() => setTab("library")} className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "library" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
          Library ({pubs.length})
        </button>
      </div>

      {tab === "requests" && (
        <div className="space-y-3">
          <div className="flex gap-1.5">
            {["pending", "approved", "denied", "all"].map(s => (
              <button key={s} data-testid={`filter-${s}`} onClick={() => setStatusFilter(s)}
                className={`text-xs px-2.5 py-1 rounded-full border capitalize ${statusFilter === s ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>{s}</button>
            ))}
          </div>
          {requests.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-300 py-12 text-center text-sm text-zinc-500" data-testid="requests-empty">
              No {statusFilter === "all" ? "" : statusFilter + " "}requests yet. Add a CTA button linking to a publication's request page in your next campaign.
            </div>
          ) : (
            <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Requester</th>
                    <th className="px-3 py-2 font-semibold">Company</th>
                    <th className="px-3 py-2 font-semibold">Publication</th>
                    <th className="px-3 py-2 font-semibold">Requested</th>
                    <th className="px-3 py-2 font-semibold">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody data-testid="requests-table-body">
                  {requests.map(r => <RequestRow key={r.id} r={r} onDecide={decide} busy={busy}/>)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "library" && (
        pubs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-300 py-12 text-center text-sm text-zinc-500" data-testid="library-empty">
            No publications yet. Click <strong>Add publication</strong> to upload your first gated document.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="publications-grid">
            {pubs.map(p => <PublicationCard key={p.id} p={p} onCopyLink={copyLink} onDelete={deletePub} onSave={savePub}/>)}
          </div>
        )
      )}

      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-xs text-zinc-600 space-y-1">
        <div className="font-semibold text-zinc-900 flex items-center gap-1.5"><PaperPlaneTilt size={13}/> How it works</div>
        <p>1 · Upload the document here and <strong>copy its request link</strong>. 2 · In your campaign, point a CTA button at that link (the Button block has a "Link to gated publication" shortcut). 3 · Recipients land on a branded form — name, company and email are pre-filled for known contacts. 4 · You get an email with one-click <strong>Approve / Deny</strong> buttons (approvals also work from this page). 5 · Approved requesters automatically receive the document as an attachment; denied ones hear nothing. New requesters are added to Contacts tagged <code>publication-request</code>.</p>
      </div>

      {showUpload && <UploadDialog onClose={() => setShowUpload(false)} onCreated={load}/>}
    </div>
  );
}
